import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize, takeUntil } from "rxjs/operators";

import { MfaMethod } from "generated/graphql";

import { LogoffService } from "auth/logoff.service";
import { MfaService } from "auth/services/mfa.service";
import { WithDisposableSubject } from "core/mixins/with-disposable-subject";
import { ToastrService } from "core/toastr.service";

@Component({
  selector: "app-verify-mfa",
  templateUrl: "./verify-mfa.component.html",
})
export class VerifyMfaComponent extends WithDisposableSubject() implements OnInit {
  mfaCode: string = "";
  errorMessages: Array<string> = [];
  isSubmitting = false;
  /** Last digits or masked value from ?maskedPhone= when SMS. */
  maskedPhone: string | null = null;
  /** True when ?mfaMethod= indicates SMS (matches GraphQL MfaMethod / Postman). */
  isSmsMfaMethod = false;

  constructor(
    private mfaService: MfaService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private logoffService: LogoffService,
  ) {
    super();
  }

  ngOnInit() {
    const { mfaMethod, maskedPhone } = this.mfaService.getMfaVerificationQueryParams(
      this.route.snapshot.queryParamMap,
    );
    this.maskedPhone = maskedPhone;
    this.isSmsMfaMethod = mfaMethod === MfaMethod.Sms;
  }

  useDifferentAccount() {
    this.logoffService.logoff();
  }

  verifyMfa() {
    if (!this.mfaCode || this.mfaCode.length !== 6) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessages = [];

    this.mfaService
      .verifyLogin(this.mfaCode)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntil(this.disposer),
      )
      .subscribe({
        next: (result) => {
          if (!result.success) {
            this.errorMessages = result.errors || [
              "Failed to verify code. Please try again.",
            ];
            this.mfaCode = "";
            return;
          }
          this.router.navigateByUrl("");
        },
        error: (err) => {
          this.errorMessages = [
            err.message || "Failed to verify code. Please try again.",
          ];
          this.mfaCode = "";
        },
      });
  }

  resendCode() {
    if (!this.isSmsMfaMethod || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessages = [];

    this.mfaService
      .resendSmsCode()
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntil(this.disposer),
      )
      .subscribe({
        next: (result) => {
          if (!result.success) {
            this.errorMessages = result.errors || [
              "Failed to resend code. Please try again.",
            ];
            return;
          }

          this.toastrService.success("Code resent to your phone number.");
        },
        error: (err) => {
          this.errorMessages = [
            err.message || "Failed to resend code. Please try again.",
          ];
        },
      });
  }
}
